import React, { useState } from "react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { FiGlobe } from "react-icons/fi";
import logo from "../assets/logo.png";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { springBouncy, useMagnetic, EXPO_OUT } from "../utils/animations";

const NAV_ITEMS = [
  { key: "home", to: "hero" },
  { key: "about", to: "about" },
  { key: "services", to: "services" },
  { key: "contact", to: "contact" },
];

/* Magnetic nav link — cursor follows with spring physics */
function MagneticNavLink({ item, i, navTextCls, t }) {
  const { ref, springX, springY, onMove, onLeave } = useMagnetic(0.3);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: -25, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ delay: 0.15 + i * 0.08, ...springBouncy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: springX, y: springY }}
    >
      <Link
        to={item.to}
        spy
        smooth
        offset={-80}
        duration={600}
        className={`relative cursor-pointer transition-colors font-cairo text-sm font-semibold group py-2 ${navTextCls}`}
        activeClass="!text-highlight"
      >
        {t(`nav.${item.key}`)}
        <motion.span
          className="absolute bottom-0 inset-x-0 h-0.5 bg-gradient-to-r from-highlight to-highlight-light rounded-full origin-center"
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.3, ease: EXPO_OUT }}
        />
      </Link>
    </motion.div>
  );
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { dark } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();

  // Auto-hide navbar on scroll down, show on scroll up
  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious();
    setScrolled(latest > 50);
    if (latest > prev && latest > 200) setHidden(true);
    else setHidden(false);
  });

  const toggleLang = () => {
    const next = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(next);
    document.documentElement.lang = next;
    document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
  };

  const navTextCls = scrolled
    ? "text-main/70 dark:text-white/70 hover:text-highlight"
    : "text-white/80 hover:text-highlight";

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: hidden ? -100 : 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 dark:bg-main/80 backdrop-blur-2xl shadow-[0_1px_20px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_20px_rgba(0,0,0,0.3)] border-b border-gray-200/50 dark:border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left: Language + Theme toggle */}
          <div className="hidden md:flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleLang}
              className={`flex items-center gap-1.5 px-4 py-2 border rounded-full transition-all text-sm font-semibold group ${
                scrolled
                  ? "border-gray-300 dark:border-white/20 text-main/70 dark:text-white/70 hover:border-highlight hover:text-highlight"
                  : "border-white/30 text-white/80 hover:border-highlight hover:text-highlight"
              }`}
            >
              {t("lang")}
              <FiGlobe className="text-base group-hover:rotate-180 transition-transform duration-500" />
            </motion.button>
            <ThemeToggle
              className={
                scrolled
                  ? "bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20"
                  : "bg-white/10 hover:bg-white/20"
              }
            />
          </div>

          {/* Desktop nav — magnetic hover + staggered entrance */}
          <div className="hidden md:flex items-center gap-10">
            {NAV_ITEMS.map((item, i) => (
              <MagneticNavLink key={item.key} item={item} i={i} navTextCls={navTextCls} t={t} />
            ))}
          </div>

          {/* Logo — blur + scale entrance */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <motion.img
              src={logo}
              alt="شركة المتاح للصرافة"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className={`h-12 w-auto transition-all duration-500 ${
                scrolled && !dark ? "brightness-0" : ""
              }`}
            />
          </motion.div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle
              className={
                scrolled
                  ? "bg-gray-100 dark:bg-white/10"
                  : "bg-white/10"
              }
            />
            <motion.button
              whileTap={{ scale: 0.85, rotate: 90 }}
              className={`text-2xl transition-colors ${
                scrolled ? "text-main dark:text-white" : "text-white"
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {menuOpen ? (
                  <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <HiX />
                  </motion.span>
                ) : (
                  <motion.span key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <HiMenuAlt3 />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu — slide down with backdrop blur */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0, filter: "blur(10px)" }}
            animate={{ opacity: 1, height: "auto", filter: "blur(0px)" }}
            exit={{ opacity: 0, height: 0, filter: "blur(10px)" }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="md:hidden bg-white/95 dark:bg-main/95 backdrop-blur-2xl px-4 pb-4 overflow-hidden border-t border-gray-200/50 dark:border-white/5"
          >
            <div className="space-y-1 pt-2">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -30, filter: "blur(4px)" }}
                  animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  transition={{ delay: i * 0.06, ...springBouncy }}
                >
                  <Link
                    to={item.to}
                    spy
                    smooth
                    offset={-64}
                    duration={600}
                    className="block text-main/80 dark:text-white/80 hover:text-highlight hover:bg-highlight/5 cursor-pointer font-cairo text-base py-3 px-3 rounded-lg transition-all"
                    activeClass="!text-highlight !bg-highlight/10"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </motion.div>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                onClick={() => { toggleLang(); setMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 mt-2 border border-highlight/40 text-highlight rounded-full hover:bg-highlight/10 transition-all text-sm font-semibold"
              >
                {t("lang")}
                <FiGlobe className="text-base" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
